import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';

const CredentialCard = ({ credential, onClick }) => {
  const [badgeImages, setBadgeImages] = useState<string[]>([]);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // Programmatically get badge images
  useEffect(() => {
    const importBadgeImages = async () => {
      try {
        // This will work if you're using a bundler like Webpack or Vite
        const context = (require as any).context('../../public/badge', true, /\.png$/);
        const imagePaths = context.keys().map((key: string) => key.replace('./', '/badge/'));
        setBadgeImages(imagePaths);
      } catch (error) {
        console.warn('Could not dynamically import badge images, using fallback');
        // Fallback to a few common images if dynamic import fails
        setBadgeImages([
          "/badge/big-data/data-scientist.png",
          "/badge/cloud-computing/cloud-architect-cloud-engineer.png",
          "/badge/cybersecurity/senior-risk-analyst.png",
          "/badge/digital-product-design/ux-designer-application-web-.png",
          "/badge/product-marketing/digital-marketing-strategist.png"
        ]);
      }
    };

    importBadgeImages();
  }, []);

  const getRandomBadgeImage = () => {
    if (badgeImages.length === 0) return "/badge/product-marketing/hs58-dm.png";
    return badgeImages[Math.floor(Math.random() * badgeImages.length)];
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx || !img) return;

    canvas.width = img?.naturalWidth || 0;
    canvas.height = img?.naturalHeight || 0;
    ctx.drawImage(img, 0, 0);

    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Find non-transparent bounds
      let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const alpha = data[(y * canvas.width + x) * 4 + 3];
          if (alpha > 0) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;

      if (contentWidth > 0 && contentHeight > 0) {
        setImageDimensions({
          width: contentWidth,
          height: contentHeight
        });
      } else {
        // Fallback to original dimensions if transparency detection fails
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      }
    } catch (error) {
      // Fallback to original dimensions
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    }
  };

  const getImageSizeClass = () => {
    if (!imageDimensions) return 'max-h-[50%] max-w-[50%]';

    const { width, height } = imageDimensions;
    const aspectRatio = width / height;

    // If image is wider than tall, constrain by height
    if (aspectRatio > 1.2) {
      return 'max-h-[50%] w-auto';
    }
    // If image is taller than wide, constrain by width
    else if (aspectRatio < 0.8) {
      return 'h-auto max-w-[50%]';
    }
    // For roughly square images, use both constraints
    else {
      return 'max-h-[50%] max-w-[50%]';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const credentialId = credential.unique_code || credential.id;
  const title = credential.name ||  credential.skillstree?.name || credentialId;
  const issuedOn = credential.earned_at || credential.completed_at;
  const image = credential.image_url || credential.image || (credential.type === 'passports' ? '/pages/achievements/skilltrue-passport.png' : '/pages/achievements/Digital-Badges.png');
  const defaultImage = credential.type === 'passports' ? '/pages/achievements/skilltrue-passport.png' : '/pages/achievements/Digital-Badges.png';

  return (
    <div 
      onClick={onClick} 
      className="max-w-xs bg-white rounded-lg overflow-hidden border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
    >
      {/* Image Container with Animated Gradient */}
      <div className="relative flex flex-col min-h-64 bg-gradient-to-br from-blue-700 via-orange-400 to-blue-950 bg-[length:100%_100%] group-hover:bg-[length:200%_200%] transition-all duration-1000 ease-in-out items-center justify-center p-4 overflow-hidden">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-700/20 via-orange-400/20 to-blue-950/20 group-hover:scale-120 transition-transform duration-1000 ease-in-out" />
        
        <img 
          className={`object-contain transition-transform duration-300 group-hover:scale-105 ${getImageSizeClass()}`}
          src={image}
          alt={title}
          onLoad={handleImageLoad}
          onError={(e) => {
            e.currentTarget.src = defaultImage;
            e.currentTarget.onload = () => handleImageLoad(e);
          }}
        />
        
        {/* Credential ID positioned at bottom of image container */}
        <span className="absolute text-nowrap bottom-12 left-1/2 transform -translate-x-1/2 text-[0.55rem] font-normal bg-black text-white rounded-full border-2 border-white py-0.5 px-2.5 z-10">
          Credential ID {credentialId}
        </span>
      </div>

      {/* Content Section */}
      <div className="px-6 py-4 bg-white text-center space-y-2">
        {/* Title */}
        <div className="flex items-center justify-center gap-2">
          <h3 className="font-normal text-black text-sm">{title}</h3>
        </div>

        {/* Date */}
        <p className="text-xs text-gray-900/80">{formatDate(issuedOn)}</p>

        {/* Issuer with CheckCircle icon */}
        <div className="flex items-center justify-center gap-1 text-blue-500 font-normal text-xs">
          <CheckCircle size={14} className="mr-1 text-green-500" />
          <span>{credential.issuer || "SKILLSTRUE"}</span>
        </div>
      </div>
    </div>
  );
};

export default CredentialCard;